import { useRef, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCheck,
  faExclamationTriangle,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import PublicNavBar from '../../components/layout/PublicNavBar';
import PublicFooter from '../../components/layout/PublicFooter';
import { usePageMeta } from '../../hooks/usePageMeta';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

type FormState = 'idle' | 'submitting' | 'success' | 'error';

function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, style: { opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(18px)', transition: 'opacity 0.7s ease, transform 0.7s ease' } };
}

export default function ResearchNotesHubPage() {
  usePageMeta({
    title: 'Research Notes',
    description:
      'Structured research on systematic trading, validation, and strategy design. Focused on robustness, not optimisation.',
  });

  const [email, setEmail] = useState('');
  const [formState, setFormState] = useState<FormState>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const revHero = useScrollReveal(0.1);
  const revComingSoon = useScrollReveal(0.15);
  const revAbout = useScrollReveal(0.15);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!email.trim()) {
      setErrorMessage('Please enter your email.');
      return;
    }

    setFormState('submitting');

    try {
      const response = await fetch(`${API_BASE}/labs/community-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Submission failed');
      }

      setFormState('success');
    } catch {
      setFormState('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="bg-[#0a0e14] text-white min-h-screen">
      <PublicNavBar />

      {/* Hero */}
      <section
        ref={revHero.ref as React.RefObject<HTMLElement>}
        style={revHero.style}
        className="pt-32 pb-16 px-4 sm:px-6 max-w-3xl mx-auto text-center"
      >
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
          Research
        </h1>
        <p className="text-[15px] text-gray-400 leading-relaxed max-w-xl mx-auto">
          Structured research on systematic trading, validation, and strategy design.
        </p>
        <p className="text-[13px] text-gray-600 mt-2">
          Focused on robustness, not optimisation.
        </p>
      </section>

      {/* Coming Soon + Email Signup */}
      <section
        ref={revComingSoon.ref as React.RefObject<HTMLElement>}
        style={revComingSoon.style}
        className="pb-20 px-4 sm:px-6 max-w-3xl mx-auto"
      >
        <div className="border border-gray-800 rounded-xl p-10 sm:p-12 bg-gray-900/20 text-center">
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-white mb-3">
            Research Notes Coming Soon
          </h2>
          <p className="text-[14px] text-gray-400 leading-relaxed max-w-md mx-auto mb-8">
            We are preparing the first set of research notes. Sign up below to be notified when they are published.
          </p>

          {formState === 'success' ? (
            <div className="flex items-center justify-center gap-2 text-emerald-400 bg-emerald-400/[0.06] border border-emerald-400/20 rounded-lg px-6 py-4 text-[14px]">
              <FontAwesomeIcon icon={faCheck} />
              <span>You're on the list. We'll let you know when the first note drops.</span>
            </div>
          ) : (
            <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 px-4 py-2.5 rounded-lg bg-gray-900/50 border border-gray-800 text-white text-[14px] placeholder-gray-600 focus:outline-none focus:border-[#9d4edd]/40 transition-colors"
                disabled={formState === 'submitting'}
              />
              <button
                type="submit"
                disabled={formState === 'submitting'}
                className="px-6 py-2.5 rounded-lg bg-[#9d4edd] hover:bg-[#8b3ec7] text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {formState === 'submitting' ? (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                ) : (
                  <>
                    Notify Me
                    <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                  </>
                )}
              </button>
            </form>
          )}

          {formState === 'error' && errorMessage && (
            <div className="mt-3 flex items-center justify-center gap-2 text-amber-400 text-[13px]">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-[11px]" />
              {errorMessage}
            </div>
          )}

          <p className="text-[11px] font-mono text-gray-600 mt-4">
            No spam. Unsubscribe anytime.
          </p>
        </div>
      </section>

      {/* About block */}
      <section
        ref={revAbout.ref as React.RefObject<HTMLElement>}
        style={revAbout.style}
        className="pb-20 px-4 sm:px-6 max-w-3xl mx-auto"
      >
        <h2 className="text-[13px] font-mono text-gray-500 tracking-wide uppercase mb-4">
          About Research
        </h2>
        <p className="text-[14px] text-gray-500 leading-relaxed">
          These notes document ongoing work in systematic trading research.
          They focus on behaviour, robustness, and validation — not just results.
          Each note is intended to be read as part of a broader research process.
        </p>
      </section>

      <PublicFooter />
    </div>
  );
}
